import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import styles from './LoginPopup.module.css';
import { Helmet } from '../utils/helmet';
import { useTranslation } from 'react-i18next';

function SignupPopup({ isOpen, onClose, onSwitchToLogin }) {
	const [formData, setFormData] = useState({
		name: '',
		username: '',
		password: '',
		email: ''
	});
	const [pfp, setPfp] = useState(null);
	const [error, setError] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
  const { signup, loginWithGoogle } = useUser();
  const navigate = typeof window !== 'undefined' ? (path) => { window.location.href = path; } : () => {};
  const { t } = useTranslation();

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0] && e.target.files[0].size > 10 * 1024 * 1024) {
      setError(t('auth.shared.fileTooLarge'));
      e.target.value = null;
      setPfp(null);
    } else {
      setPfp(e.target.files[0]);
      setError('');
    }
  };

  const resetForm = () => {
		setFormData({ name: '', username: '', password: '', email: '' });
		setPfp(null);
		setError('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setLoading(true);

    const result = await signup({
      email: formData.email,
      password: formData.password,
      username: formData.username,
      nickname: formData.name,
      pfp
    });

    if (result.success) {
      resetForm();
      onClose();
      navigate('/browse');
    } else {
      setError(result.message);
    }
    setLoading(false);
	};

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setLoading(false);
      setGoogleLoading(false);
    }
  }, [isOpen]);

  const handleGoogleSignup = async () => {
		setError('');
		setGoogleLoading(true);
    const result = await loginWithGoogle();
    if (result.success) {
      resetForm();
      onClose();
      navigate('/browse');
    } else {
      setError(result.message);
    }
    setGoogleLoading(false);
	};

  if (!isOpen) return null;

  return (
    <>
      <Helmet>
        <title>{t('auth.signup.helmetTitle')}</title>
        <meta name="description" content={t('auth.signup.helmetDescription')} />
      </Helmet>
    <div className={styles.modalOverlay} onClick={() => { resetForm(); onClose(); }}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={() => { resetForm(); onClose(); }}>×</button>

        <div className={styles.modalBody}>
          <h2>{t('auth.signup.title')}</h2>

          <form onSubmit={handleSubmit} className={styles.form}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.formGroup}>
              <label htmlFor="name">{t('auth.signup.fields.nickname')}:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="username">{t('auth.signup.fields.username')}:</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">{t('auth.shared.email')}:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t('auth.shared.emailPlaceholder')}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">{t('auth.shared.password')}:</label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder={t('auth.signup.passwordPlaceholder')}
                value={formData.password}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="pfp">{t('auth.signup.fields.pfp')}:</label>
              <input
                type="file"
                id="pfp"
                name="pfp"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup} style={{ alignItems: 'flex-start' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  style={{ width: '1rem', height: '1rem' }}
                  required
                />
                <span style={{ fontSize: '0.95rem' }}>
                  {t('auth.signup.acceptTosPrefix', 'I agree to the')}{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">{t('common.termsOfService', 'Terms of Service')}</a>
                </span>
              </label>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loading || !tosAccepted}>
              {loading ? t('auth.signup.submitting') : t('auth.signup.submit')}
            </button>
          </form>

          <div style={{ margin: '0.75rem 0' }}>
            <div style={{ margin: '0.5rem 0', color: 'var(--secondary-text-color)' }}>{t('auth.shared.or')}</div>
            <button
              type="button"
              className={styles.googleBtn}
              onClick={handleGoogleSignup}
              disabled={googleLoading}
            >
              {googleLoading ? t('auth.shared.googleLoading') : t('auth.shared.google')}
            </button>
          </div>

          <div className={styles.switchAuth}>
            <p>{t('auth.signup.switchPrompt')}</p>
            <button onClick={onSwitchToLogin} className={styles.switchBtn}>
              {t('auth.signup.switchAction')}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default SignupPopup;
